import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import type { RowSelectionState, SortingState } from "@tanstack/react-table";
import { useDebounce } from "../hooks/useDebounce";
import {
  exportReceivablesCsv,
  useReceivablesCustomersQuery,
  useReceivablesInvoicesQuery,
  useReceivablesSummaryQuery,
} from "../api/queries/receivablesQueries";
import { useCurrentCurrency } from "../hooks/useCurrentCurrency";
import { useConsolidated } from "../hooks/useConsolidated";
import { coerceSupportedCurrency, normalizeCurrency } from "../lib/currency";
import type { Currency } from "../types";
import { formatCurrency } from "./cashflow/CashflowContext";
import { ReceivablesHeaderToolbar } from "./receivables/ReceivablesHeaderToolbar";
import { ReceivablesStatusStrip } from "./receivables/ReceivablesStatusStrip";
import { ReceivablesSharedControls } from "./receivables/ReceivablesSharedControls";
import { ReceivablesCustomersTable } from "./receivables/ReceivablesCustomersTable";
import { ReceivablesInvoicesTable } from "./receivables/ReceivablesInvoicesTable";
import { ReceivablesEmailsInbox } from "./receivables/emails/ReceivablesEmailsInbox";
import { ReceivablesPagination } from "./receivables/ReceivablesPagination";
import {
  receivablesCustomerSelectionColumn,
  receivablesCustomerEmailStatusColumn,
  receivablesCustomersColumns,
} from "./receivables/receivablesCustomersColumns";
import { CustomerGroupBulkModal } from "./receivables/CustomerGroupBulkModal";
import { CashAssistantPanel } from "./cashChat/CashAssistantPanel";
import { useAuth } from "../hooks/useAuth";
import { useOrgUsersQuery } from "../api/queries/orgUsersQueries";
import { useCustomerGroupsQuery } from "../api/queries/customerGroupsQueries";
import {
  receivablesInvoiceSelectionColumn,
  receivablesInvoiceEmailStatusColumn,
  createReceivablesInvoicesColumns,
  createReceivablesInvoiceActionsColumn,
} from "./receivables/receivablesInvoicesColumns";
import { ReceivablesBulkActionsMenu } from "./InvoiceBulkActionsMenu";
import { InvoiceFilesModal } from "./receivables/InvoiceFilesModal";
import { InvoiceDetailModal } from "./receivables/InvoiceDetailModal";
import { CustomerDetailModal } from "./receivables/CustomerDetailModal";
import { warmupInvoicesPdf } from "../api/queries/paymentsQueries";
import { DataTableSkeleton, getSortedRowData, useDataTable } from "./table";
import type {
  CustomerReceivablesRow,
  InvoiceReceivablesRow,
  RiskLevelFilters,
  InvoiceTypeFilter,
  EscalationFilter,
  EmailActivityFilter,
  StatusFilters,
  StatusTile,
  EscalationTile,
  ReceivablesStripTile,
  ViewBy,
  EmailSentByFilter,
} from "./receivables/types";
import {
  readReceivablesTableState,
  writeReceivablesTableState,
} from "../utils/receivablesTableStateStorage";
import type { IntentionStatus } from "../utils/types";

function formatYmdLocal(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

const DEFAULT_CUSTOMERS_SORT: SortingState = [
  { id: "totalOpenBalance", desc: true },
];
const DEFAULT_INVOICES_SORT: SortingState = [{ id: "amount", desc: true }];
const RECEIVABLES_PAGE_SIZE_OPTIONS = [10, 15, 25, 50] as const;
const EMPTY_CUSTOMER_ROWS: CustomerReceivablesRow[] = [];
const EMPTY_INVOICE_ROWS: InvoiceReceivablesRow[] = [];

function buildDefaultDateRange() {
  const dt = new Date();
  dt.setFullYear(dt.getFullYear() - 1);
  return {
    startDate: formatYmdLocal(dt),
    endDate: formatYmdLocal(new Date()),
  };
}

const AGING_URL_VALUES: ReadonlySet<string> = new Set([
  "current",
  "0_30",
  "31_60",
  "61_90",
  "90_plus",
]);

/**
 * Deep-link filters, e.g. `/receivables?aging=90_plus&view=customers` from
 * clicking a slice of the dashboard aging chart. A link is an explicit
 * intent, so it wins over whatever filter was last persisted.
 */
function readDeepLinkFilters(search: string): {
  riskLevels?: RiskLevelFilters;
  viewBy?: ViewBy;
} {
  const params = new URLSearchParams(search);
  const result: { riskLevels?: RiskLevelFilters; viewBy?: ViewBy } = {};

  const aging = params.get("aging");
  if (aging && AGING_URL_VALUES.has(aging)) {
    result.riskLevels = [aging as RiskLevelFilters[number]];
  }
  const view = params.get("view");
  if (view === "customers" || view === "invoices" || view === "emails") {
    result.viewBy = view;
  }
  return result;
}

function getInitialReceivablesTableState() {
  const stored = readReceivablesTableState();
  const defaultDates = buildDefaultDateRange();
  const deepLink = readDeepLinkFilters(
    typeof window === "undefined" ? "" : window.location.search,
  );
  const viewBy = deepLink.viewBy ?? stored?.viewBy ?? "customers";
  return {
    search: deepLink.riskLevels ? "" : (stored?.search ?? ""),
    viewBy,
    riskLevels: deepLink.riskLevels ?? stored?.riskLevels ?? [],
    invoiceTypeFilter:
      stored?.invoiceTypeFilter ?? ("all" as InvoiceTypeFilter),
    emailActivityFilter: "all" as EmailActivityFilter,
    escalationFilter: stored?.escalationFilter ?? ("all" as EscalationFilter),
    statusFilters: stored?.statusFilters ?? [],
    sorting:
      stored?.sorting ??
      (viewBy === "invoices" ? DEFAULT_INVOICES_SORT : DEFAULT_CUSTOMERS_SORT),
    page: stored?.page ?? 1,
    pageSize: stored?.pageSize ?? 10,
    startDate: stored?.startDate ?? defaultDates.startDate,
    endDate: stored?.endDate ?? defaultDates.endDate,
    filterCollectorId: stored?.filterCollectorId ?? ("" as const),
    filterGroupId: stored?.filterGroupId ?? ("" as const),
    filterSentBy: stored?.filterSentBy ?? ("" as const),
  };
}

const ReceivablesCenter = () => {
  const navigate = useNavigate();
  const initialTableState = useMemo(
    () => getInitialReceivablesTableState(),
    [],
  );

  // The deep-link params have been folded into state above; drop them from
  // the URL so a later refresh doesn't re-apply a filter the user cleared.
  useEffect(() => {
    if (typeof window === "undefined" || !window.location.search) return;
    const params = new URLSearchParams(window.location.search);
    if (!params.has("aging") && !params.has("view")) return;
    params.delete("aging");
    params.delete("view");
    const query = params.toString();
    navigate(`/receivables${query ? `?${query}` : ""}`, { replace: true });
  }, [navigate]);
  const [search, setSearch] = useState(initialTableState.search);
  const debouncedSearch = useDebounce(search.trim() || undefined, 400);
  const [viewBy, setViewBy] = useState<ViewBy>(initialTableState.viewBy);
  const [riskLevels, setRiskLevels] = useState<RiskLevelFilters>(
    initialTableState.riskLevels,
  );
  const [invoiceTypeFilter, setInvoiceTypeFilter] = useState<InvoiceTypeFilter>(
    initialTableState.invoiceTypeFilter,
  );
  const [emailActivityFilter, setEmailActivityFilter] =
    useState<EmailActivityFilter>(initialTableState.emailActivityFilter);
  const [escalationFilter, setEscalationFilter] = useState<EscalationFilter>(
    initialTableState.escalationFilter,
  );
  const [statusFilters, setStatusFilters] = useState<StatusFilters>(
    initialTableState.statusFilters,
  );
  const [sorting, setSorting] = useState<SortingState>(
    initialTableState.sorting,
  );
  const [invoiceRowSelection, setInvoiceRowSelection] =
    useState<RowSelectionState>({});
  const [invoiceFilesModalIds, setInvoiceFilesModalIds] = useState<
    string[] | null
  >(null);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string | null>(
    null,
  );
  const [selectedCustomerId, setSelectedCustomerId] = useState<number | null>(
    null,
  );

  const openInvoiceDetail = useCallback((externalId: string) => {
    setSelectedCustomerId(null);
    setSelectedInvoiceId(externalId);
  }, []);

  const openCustomerDetail = useCallback((customerId: number) => {
    setSelectedInvoiceId(null);
    setSelectedCustomerId(customerId);
  }, []);
  const [page, setPage] = useState(initialTableState.page);
  const [pageSize, setPageSize] = useState(initialTableState.pageSize);
  const { isOrgManager, isOrgCollector, hasFeature } = useAuth();
  const showInvoiceTypeFilter = hasFeature("RECEIVABLES_PI_3RD_WIDGET");
  const [filterCollectorId, setFilterCollectorId] = useState<number | "">(
    initialTableState.filterCollectorId,
  );
  const [filterGroupId, setFilterGroupId] = useState<number | "">(
    initialTableState.filterGroupId,
  );
  const [filterSentBy, setFilterSentBy] = useState<EmailSentByFilter>(
    initialTableState.filterSentBy,
  );
  const [startDate, setStartDate] = useState<string | undefined>(
    initialTableState.startDate,
  );
  const [endDate, setEndDate] = useState<string | undefined>(
    initialTableState.endDate,
  );
  const [customerRowSelection, setCustomerRowSelection] =
    useState<RowSelectionState>({});
  const [groupBulkMode, setGroupBulkMode] = useState<
    "assign" | "remove" | null
  >(null);
  const { data: orgUsersData } = useOrgUsersQuery({ enabled: isOrgManager });
  const { data: customerGroups = [] } = useCustomerGroupsQuery({
    enabled: isOrgManager || isOrgCollector,
  });

  const managerFilters = useMemo(() => {
    const customerGroupId = filterGroupId === "" ? undefined : filterGroupId;
    if (isOrgManager) {
      return {
        collectorId: filterCollectorId === "" ? undefined : filterCollectorId,
        customerGroupId,
      };
    }
    if (isOrgCollector && customerGroupId != null) {
      return { customerGroupId };
    }
    return undefined;
  }, [isOrgManager, isOrgCollector, filterCollectorId, filterGroupId]);

  useEffect(() => {
    if (!isOrgCollector || filterGroupId === "") return;
    if (customerGroups.length === 0) return;
    if (!customerGroups.some((g) => g.id === filterGroupId)) {
      setFilterGroupId("");
      setPage(1);
    }
  }, [isOrgCollector, customerGroups, filterGroupId]);

  useEffect(() => {
    writeReceivablesTableState({
      search,
      viewBy,
      riskLevels,
      invoiceTypeFilter,
      escalationFilter,
      statusFilters,
      sorting,
      page,
      pageSize,
      startDate,
      endDate,
      filterCollectorId,
      filterGroupId,
      filterSentBy,
    });
  }, [
    search,
    viewBy,
    riskLevels,
    invoiceTypeFilter,
    escalationFilter,
    statusFilters,
    sorting,
    page,
    pageSize,
    startDate,
    endDate,
    filterCollectorId,
    filterGroupId,
    filterSentBy,
  ]);

  const handleSortingChange = (
    updater: SortingState | ((prev: SortingState) => SortingState),
  ) => {
    setSorting(updater);
    setPage(1);
  };

  const handlePageSizeChange = (nextPageSize: number) => {
    setPageSize(nextPageSize);
    setPage(1);
  };

  const { currency, setCurrency } = useCurrentCurrency();
  const { effectiveConsolidated } = useConsolidated();
  const didAutoSwitchCurrency = useRef(false);

  const summaryQuery = useReceivablesSummaryQuery(
    currency,
    startDate,
    endDate,
    managerFilters,
    effectiveConsolidated,
  );
  const effectiveCustomersSorting: SortingState =
    sorting[0]?.id === "amount" ? DEFAULT_CUSTOMERS_SORT : sorting;
  const effectiveInvoicesSorting: SortingState =
    sorting[0]?.id === "totalOpenBalance" ? DEFAULT_INVOICES_SORT : sorting;

  useEffect(() => {
    if (
      effectiveConsolidated ||
      didAutoSwitchCurrency.current ||
      summaryQuery.isLoading ||
      !summaryQuery.data
    ) {
      return;
    }
    const { unpaidInvoicesCount, availableCurrencies } = summaryQuery.data;
    const paymentCurrencies = [
      ...new Set(availableCurrencies.map((c) => normalizeCurrency(c))),
    ];
    if (unpaidInvoicesCount > 0 || paymentCurrencies.length === 0) {
      return;
    }
    const current = normalizeCurrency(currency);
    if (paymentCurrencies.includes(current)) {
      return;
    }
    didAutoSwitchCurrency.current = true;
    setCurrency(coerceSupportedCurrency(paymentCurrencies[0]) as Currency);
  }, [
    summaryQuery.data,
    summaryQuery.isLoading,
    currency,
    setCurrency,
    effectiveConsolidated,
  ]);
  const invoicesQuery = useReceivablesInvoicesQuery({
    page,
    pageSize,
    currency,
    consolidated: effectiveConsolidated,
    search: debouncedSearch,
    riskLevels,
    statusFilters,
    sorting: effectiveInvoicesSorting,
    startDate,
    endDate,
    managerFilters,
    invoiceTypeFilter: showInvoiceTypeFilter ? invoiceTypeFilter : undefined,
    escalationFilter,
    emailActivityFilter,
    enabled: viewBy === "invoices",
  });
  const customersQuery = useReceivablesCustomersQuery({
    page,
    pageSize,
    currency,
    consolidated: effectiveConsolidated,
    search: debouncedSearch,
    riskLevels,
    statusFilters,
    sorting: effectiveCustomersSorting,
    startDate,
    endDate,
    managerFilters,
    invoiceTypeFilter: showInvoiceTypeFilter ? invoiceTypeFilter : undefined,
    enabled: viewBy === "customers",
  });
  const tableQuery =
    viewBy === "customers"
      ? customersQuery
      : viewBy === "invoices"
        ? invoicesQuery
        : null;
  const customerRows = customersQuery.data?.rows ?? EMPTY_CUSTOMER_ROWS;
  const invoiceRows = invoicesQuery.data?.rows ?? EMPTY_INVOICE_ROWS;
  const totalRows = tableQuery?.data?.total ?? 0;
  const filteredInvoicesTotalAmount =
    viewBy === "invoices" ? (invoicesQuery.data?.totalAmount ?? 0) : 0;
  const totalPages = Math.max(1, Math.ceil(totalRows / pageSize));
  const isTableBusy =
    Boolean(tableQuery?.isLoading) || Boolean(tableQuery?.isFetching);

  useEffect(() => {
    if (viewBy === "emails") return;
    const total = tableQuery?.data?.total;
    if (total == null) return;
    const maxPage = Math.max(1, Math.ceil(total / pageSize));
    if (page > maxPage) setPage(maxPage);
  }, [page, tableQuery?.data?.total, viewBy, pageSize]);

  const customerColumns = useMemo(
    () => [
      receivablesCustomerSelectionColumn,
      receivablesCustomerEmailStatusColumn,
      ...receivablesCustomersColumns,
    ],
    [],
  );

  const customersTable = useDataTable({
    data: customerRows,
    columns: customerColumns,
    getRowId: (row) => String(row.customerId),
    sorting:
      viewBy === "customers"
        ? effectiveCustomersSorting
        : DEFAULT_CUSTOMERS_SORT,
    onSortingChange: handleSortingChange,
    manualSorting: true,
    enableRowSelection: viewBy === "customers",
    rowSelection: customerRowSelection,
    onRowSelectionChange: setCustomerRowSelection,
  });

  const handleViewInvoiceFiles = useCallback((invoiceIds: string[]) => {
    if (invoiceIds.length === 0) return;
    setInvoiceFilesModalIds(invoiceIds);
  }, []);

  const invoiceTableColumns = useMemo(
    () => [
      receivablesInvoiceSelectionColumn,
      // Invoice # sits directly beside the checkbox; the escalation /
      // email-disabled markers follow the identifying columns rather than
      // pushing them right with an empty-header column.
      ...createReceivablesInvoicesColumns({
        onOpenCustomer: openCustomerDetail,
        emailStatusColumn: receivablesInvoiceEmailStatusColumn,
        emailActivityFilter,
        onEmailActivityFilterChange: (value) => {
          setEmailActivityFilter(value);
          setPage(1);
        },
      }),
      createReceivablesInvoiceActionsColumn({
        onViewFile: (externalId) => handleViewInvoiceFiles([externalId]),
      }),
    ],
    [handleViewInvoiceFiles, openCustomerDetail, emailActivityFilter],
  );

  const invoicesTable = useDataTable({
    data: invoiceRows,
    columns: invoiceTableColumns,
    getRowId: (row) => row.externalId,
    sorting:
      viewBy === "invoices" ? effectiveInvoicesSorting : DEFAULT_INVOICES_SORT,
    onSortingChange: handleSortingChange,
    manualSorting: true,
    rowSelection: invoiceRowSelection,
    onRowSelectionChange: setInvoiceRowSelection,
    enableRowSelection: viewBy === "invoices",
  });

  const selectedInvoiceIds = useMemo(
    () => Object.keys(invoiceRowSelection),
    [invoiceRowSelection],
  );

  const hasSelectedPiInvoices = useMemo(
    () =>
      selectedInvoiceIds.some((id) => id.trim().toUpperCase().startsWith("PI")),
    [selectedInvoiceIds],
  );

  useEffect(() => {
    if (viewBy !== "invoices" || !hasSelectedPiInvoices) return;
    const timer = window.setTimeout(() => {
      void warmupInvoicesPdf().catch(() => {});
    }, 400);
    return () => window.clearTimeout(timer);
  }, [viewBy, hasSelectedPiInvoices, selectedInvoiceIds]);
  const selectedCustomerIds = useMemo(
    () => Object.keys(customerRowSelection).map((id) => Number(id)),
    [customerRowSelection],
  );

  const displayRows =
    viewBy === "customers"
      ? getSortedRowData(customersTable)
      : viewBy === "invoices"
        ? getSortedRowData(invoicesTable)
        : [];

  const handleViewByChange = (value: ViewBy) => {
    setViewBy(value);
    setPage(1);
    if (value === "customers") {
      setSorting(DEFAULT_CUSTOMERS_SORT);
    } else if (value === "invoices") {
      setSorting(DEFAULT_INVOICES_SORT);
    }
    if (value !== "invoices") {
      setEscalationFilter("all");
      setInvoiceRowSelection({});
    }
    if (value !== "customers") {
      setCustomerRowSelection({});
    }
  };

  const receivablesDisplayCurrency = summaryQuery.data?.currency ?? currency;
  const statusTiles: StatusTile[] = summaryQuery.data?.statusTiles ?? [];
  const escalatedTile: EscalationTile = summaryQuery.data?.escalationTile ?? {
    key: "escalated",
    label: "Escalated",
    count: 0,
    amount: 0,
    icon: "escalated",
  };

  const statusStripTiles = useMemo(() => {
    const tiles: ReceivablesStripTile[] = statusTiles
      .filter((tile) => tile.key !== "all")
      .map((tile) => ({
        ...tile,
        filterGroup: "status" as const,
      }));
    if (viewBy === "invoices") {
      tiles.push({
        ...escalatedTile,
        filterGroup: "escalation",
      });
    }
    return tiles;
  }, [statusTiles, escalatedTile, viewBy]);

  const handleStatusStripTileClick = useCallback(
    (tile: ReceivablesStripTile) => {
      if (tile.filterGroup === "escalation") {
        setStatusFilters([]);
        setEscalationFilter((current) =>
          current === "escalated" ? "all" : "escalated",
        );
      } else {
        const status = tile.key as IntentionStatus;
        setEscalationFilter("all");
        setStatusFilters((current) =>
          current.includes(status)
            ? current.filter((value) => value !== status)
            : [...current, status],
        );
      }
      setPage(1);
    },
    [],
  );

  const isStatusStripTileActive = useCallback(
    (tile: ReceivablesStripTile) => {
      if (tile.filterGroup === "escalation") {
        return escalationFilter === "escalated";
      }
      return statusFilters.includes(tile.key as IntentionStatus);
    },
    [escalationFilter, statusFilters],
  );
  return (
    <div className="flex w-full flex-col gap-5 pb-10 pt-2">
      <section className="rounded-xl border border-slate-200 bg-white px-4 py-5 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight text-slate-900">
              Receivables
            </h1>
            <p className="mt-1 text-sm text-slate-500">
              Track outstanding invoices and payment timelines
            </p>
          </div>
          <ReceivablesHeaderToolbar
            showCollectorFilter={isOrgManager}
            showGroupFilter={
              isOrgManager || (isOrgCollector && customerGroups.length > 0)
            }
            filterCollectorId={filterCollectorId}
            onFilterCollectorIdChange={(value) => {
              setFilterCollectorId(value);
              setPage(1);
            }}
            collectors={(orgUsersData?.members ?? [])
              .filter((m) => m.org_role === "COLLECTOR")
              .map((m) => ({ id: m.id, username: m.username }))}
            filterGroupId={filterGroupId}
            onFilterGroupIdChange={(value) => {
              setFilterGroupId(value);
              setPage(1);
            }}
            groups={customerGroups.map((g) => ({ id: g.id, name: g.name }))}
            startDate={startDate}
            endDate={endDate}
            onDateRangeChange={(from, to) => {
              setStartDate(from);
              setEndDate(to);
              setPage(1);
            }}
          />
        </div>
      </section>

      <section className="mb-4 flex flex-col rounded-xl border border-slate-200 bg-white shadow-sm">
        {viewBy !== "emails" ? (
          <div className="shrink-0">
            <ReceivablesStatusStrip
              tiles={statusStripTiles}
              currency={receivablesDisplayCurrency}
              isActive={isStatusStripTileActive}
              onTileClick={handleStatusStripTileClick}
            />
          </div>
        ) : null}
        <div className="shrink-0">
          <ReceivablesSharedControls
            search={search}
            onSearchChange={(value) => {
              setSearch(value);
              setPage(1);
            }}
            viewBy={viewBy}
            onViewByChange={handleViewByChange}
            riskLevels={riskLevels}
            onRiskLevelsChange={(value) => {
              setRiskLevels(value);
              setPage(1);
            }}
            invoiceTypeFilter={invoiceTypeFilter}
            onInvoiceTypeFilterChange={(value) => {
              setInvoiceTypeFilter(value);
              setPage(1);
            }}
            showInvoiceTypeFilter={showInvoiceTypeFilter}
            onDownload={() =>
              void exportReceivablesCsv({
                viewBy,
                currency,
                consolidated: effectiveConsolidated,
                search: debouncedSearch,
                riskLevels,
                statusFilters,
                sorting,
                startDate,
                endDate,
                managerFilters,
                invoiceTypeFilter: showInvoiceTypeFilter
                  ? invoiceTypeFilter
                  : undefined,
                escalationFilter,
              })
            }
            bulkActions={
              viewBy === "invoices" ? (
                <ReceivablesBulkActionsMenu
                  scope="invoices"
                  selectedInvoiceIds={selectedInvoiceIds}
                  onViewFiles={handleViewInvoiceFiles}
                />
              ) : viewBy === "customers" ? (
                <ReceivablesBulkActionsMenu
                  scope="customers"
                  selectedCustomerIds={selectedCustomerIds}
                />
              ) : null
            }
          />
        </div>

        {viewBy === "emails" ? (
          <div className="h-[min(65vh,42rem)] min-h-80 overflow-hidden">
            <ReceivablesEmailsInbox
              search={debouncedSearch}
              page={page}
              onPageChange={setPage}
              filterSentBy={filterSentBy}
              onFilterSentByChange={(value) => {
                setFilterSentBy(value);
                setPage(1);
              }}
              onOpenInvoice={openInvoiceDetail}
              onOpenCustomer={openCustomerDetail}
            />
          </div>
        ) : (
          <div className="max-h-[min(65vh,42rem)] overflow-x-auto overflow-y-auto">
            {viewBy === "customers" &&
            isOrgManager &&
            selectedCustomerIds.length > 0 ? (
              <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 px-4 py-2">
                <span className="text-sm text-slate-600">
                  {selectedCustomerIds.length} selected
                </span>
                <button
                  type="button"
                  onClick={() => setGroupBulkMode("assign")}
                  className="rounded-md bg-teal-600 px-3 py-1.5 text-sm font-medium text-white"
                >
                  Assign group
                </button>
                <button
                  type="button"
                  onClick={() => setGroupBulkMode("remove")}
                  className="rounded-md border border-slate-200 px-3 py-1.5 text-sm font-medium text-slate-700"
                >
                  Remove from group
                </button>
              </div>
            ) : null}
            {tableQuery?.isError || summaryQuery.isError ? (
              <div className="flex flex-col items-center gap-3 px-4 py-8 text-center">
                <p className="text-sm text-red-600">
                  Failed to load receivables.
                </p>
                <button
                  type="button"
                  onClick={() => {
                    if (summaryQuery.isError) void summaryQuery.refetch();
                    if (tableQuery?.isError) void tableQuery.refetch();
                  }}
                  className="rounded-md bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-700"
                >
                  Try again
                </button>
              </div>
            ) : isTableBusy && !tableQuery?.data ? (
              viewBy === "customers" ? (
                <DataTableSkeleton table={customersTable} rowCount={pageSize} />
              ) : (
                <DataTableSkeleton table={invoicesTable} rowCount={pageSize} />
              )
            ) : displayRows.length > 0 ? (
              viewBy === "customers" ? (
                <ReceivablesCustomersTable
                  table={customersTable}
                  displayRows={displayRows as CustomerReceivablesRow[]}
                  onOpenCustomer={openCustomerDetail}
                />
              ) : (
                <ReceivablesInvoicesTable
                  table={invoicesTable}
                  displayRows={displayRows as InvoiceReceivablesRow[]}
                  onOpenInvoice={openInvoiceDetail}
                />
              )
            ) : (
              <div className="px-4 py-8 text-center text-sm text-slate-500">
                No receivables match the selected filters.
              </div>
            )}
          </div>
        )}

        {viewBy !== "emails" ? (
          <ReceivablesPagination
            page={page}
            totalPages={totalPages}
            pageSize={pageSize}
            pageSizeOptions={RECEIVABLES_PAGE_SIZE_OPTIONS}
            onPageChange={setPage}
            onPageSizeChange={handlePageSizeChange}
            isFetching={tableQuery?.isFetching}
            summary={
              viewBy === "invoices" ? (
                <>
                  {totalRows} {totalRows === 1 ? "invoice" : "invoices"} ·{" "}
                  {formatCurrency(
                    filteredInvoicesTotalAmount,
                    receivablesDisplayCurrency as Currency,
                  )}{" "}
                  total
                </>
              ) : (
                <>{totalRows} transactions total</>
              )
            }
          />
        ) : null}
      </section>

      <InvoiceDetailModal
        invoiceId={selectedInvoiceId}
        opened={selectedInvoiceId !== null}
        onClose={() => setSelectedInvoiceId(null)}
        onCustomerClick={openCustomerDetail}
      />
      <CustomerDetailModal
        customerId={selectedCustomerId}
        opened={selectedCustomerId !== null}
        onClose={() => setSelectedCustomerId(null)}
        onOpenInvoice={openInvoiceDetail}
      />
      <InvoiceFilesModal
        invoiceIds={invoiceFilesModalIds ?? []}
        isOpen={invoiceFilesModalIds !== null}
        onClose={() => setInvoiceFilesModalIds(null)}
      />
      {groupBulkMode ? (
        <CustomerGroupBulkModal
          customerIds={selectedCustomerIds}
          mode={groupBulkMode}
          onClose={() => setGroupBulkMode(null)}
          onDone={() => {
            setCustomerRowSelection({});
            void customersQuery.refetch();
          }}
        />
      ) : null}
      <CashAssistantPanel domain="receivables" />
    </div>
  );
};

export default ReceivablesCenter;